import * as React from 'react'
import { useDispatch } from 'react-redux'
import { apiCallWrapperNoDispatch, defaultLabelSort, NotificationModel, NotificationsStore, NotificationType, SelectFormField, SelectOptionModel } from 'traveldesk-ui'

interface Props {
    tourId: number
    sourceLanguageId: number
    languages: SelectOptionModel[]
    previewLanguageId: number
    onPreviewLanguageChange: (languageId: number) => void
}

interface TranslationProgress {
    languageId: number
    label: string
    status: 'Queued' | 'Translating' | 'Completed' | 'Failed'
    detail?: string
}

const ADD_TRANSLATION = -1
const ALL_LANGUAGES = -2

const providerNames = (providers: string[] = []) => providers.map(provider => {
    switch (provider.toLowerCase()) {
        case 'openai': return 'OpenAI'
        case 'azure': return 'Azure Translator'
        default: return provider
    }
}).join(' and ')

const TourTranslations = ({ tourId, sourceLanguageId, languages, previewLanguageId, onPreviewLanguageChange }: Props) => {
    const dispatch = useDispatch()
    const [translatedLanguages, setTranslatedLanguages] = React.useState<SelectOptionModel[]>([])
    const [selectedLanguageId, setSelectedLanguageId] = React.useState<number>()
    const [progress, setProgress] = React.useState<TranslationProgress[]>([])
    const [isRunning, setIsRunning] = React.useState(false)
    const [loadError, setLoadError] = React.useState(false)
    const mounted = React.useRef(false)
    const running = React.useRef(false)
    const availableLanguages = languages.filter(language => Number(language.value) > 0 && Number(language.value) !== sourceLanguageId)
    const isAllLanguages = previewLanguageId === ALL_LANGUAGES

    const refreshTranslations = async () => {
        try {
            const response = await apiCallWrapperNoDispatch<SelectOptionModel[]>(`/api/tours/translatedto/${tourId}`, 'GET')
            if (!mounted.current) return
            setLoadError(!response.IsOk)
            if (response.IsOk) setTranslatedLanguages([...(response.Content ?? [])].sort(defaultLabelSort))
        } catch {
            if (mounted.current) setLoadError(true)
        }
    }

    React.useEffect(() => {
        mounted.current = true
        refreshTranslations()
        // Stop scheduling further languages when this tour's panel is unmounted.
        return () => { mounted.current = false }
    }, [tourId])

    const notify = (type: NotificationType, message: string) => dispatch(
        NotificationsStore.actionCreators.add(new NotificationModel(type, 'Tour translation', message)),
    )

    const translateLanguage = async (languageId: number) => {
        const response = await apiCallWrapperNoDispatch<{ providers?: string[] }>(`/api/tours/translate/${tourId}?toLanguage=${languageId}`, 'GET')
        if (!response.IsOk) {
            throw new Error(typeof response.ErrorMessage === 'string' && response.ErrorMessage
                ? response.ErrorMessage : response.ErrorTitle || 'Translation failed. Please try again.')
        }
        return providerNames(response.Content?.providers)
    }

    const addTranslation = async () => {
        if (running.current) return
        const targets = isAllLanguages ? availableLanguages : availableLanguages.filter(language => Number(language.value) === selectedLanguageId)
        if (targets.length === 0) return
        running.current = true
        setIsRunning(true)

        if (isAllLanguages) {
            setProgress(targets.map(language => ({ languageId: Number(language.value), label: language.label, status: 'Queued' })))
        }
        const updateProgress = (languageId: number, status: TranslationProgress['status'], detail?: string) => {
            if (mounted.current) setProgress(current => current.map(item => item.languageId === languageId ? { ...item, status, detail } : item))
        }

        let completed = 0
        try {
            for (const language of targets) {
                if (!mounted.current) break
                const languageId = Number(language.value)
                updateProgress(languageId, 'Translating')
                try {
                    const providers = await translateLanguage(languageId)
                    if (!mounted.current) break
                    completed++
                    updateProgress(languageId, 'Completed', providers ? `Translated with ${providers}` : 'No translation service was reported.')
                    if (!isAllLanguages) {
                        notify(providers ? NotificationType.success : NotificationType.information,
                            providers ? `Successfully translated with ${providers}` : 'Translation saved. No translation service was reported.')
                        setSelectedLanguageId(undefined)
                        onPreviewLanguageChange(languageId)
                    }
                } catch (error) {
                    if (!mounted.current) break
                    const message = error instanceof Error ? error.message : 'Translation failed. Please try again.'
                    updateProgress(languageId, 'Failed', message)
                    if (!isAllLanguages) notify(NotificationType.error, message)
                }
            }
            if (mounted.current) {
                if (isAllLanguages) notify(completed === targets.length ? NotificationType.success : NotificationType.warning,
                    `${completed} of ${targets.length} languages translated${completed < targets.length ? `. ${targets.length - completed} failed.` : '.'}`)
                await refreshTranslations()
            }
        } finally {
            running.current = false
            if (mounted.current) setIsRunning(false)
        }
    }

    const completedCount = progress.filter(item => item.status === 'Completed').length
    const failedCount = progress.filter(item => item.status === 'Failed').length

    return <div style={{ backgroundColor: 'white', padding: 10, position: 'sticky', bottom: 0 }}>
        {progress.length > 0 ? <section aria-label='Translation progress'>
            <strong>{isRunning ? 'Translating languages' : 'Translation finished'}</strong>
            <p role='status'>{completedCount} completed, {failedCount} failed, {progress.length - completedCount - failedCount} remaining</p>
            <progress aria-label='Translation progress' value={completedCount + failedCount} max={progress.length} style={{ width: '100%' }} />
            {isRunning && <p>Keep this page open until translation finishes.</p>}
            <ul style={{ listStyle: 'none', padding: 0, maxHeight: '45vh', overflowY: 'auto' }}>
                {progress.map(item => <li key={item.languageId} style={{ padding: '8px 0', borderBottom: '1px solid #ddd' }}>
                    <strong>{item.label}</strong>
                    <div style={{ color: item.status === 'Failed' ? '#a12622' : item.status === 'Completed' ? '#25733b' : undefined }}>{item.status}</div>
                    {item.detail && <small>{item.detail}</small>}
                </li>)}
            </ul>
            {!isRunning && <button type='button' className='button has-glow' onClick={() => { setProgress([]); onPreviewLanguageChange(0) }}>Done</button>}
        </section> : <>
            <SelectFormField
                label='Translations'
                value={previewLanguageId}
                disabled={isRunning}
                onChange={(option: SelectOptionModel) => { setSelectedLanguageId(undefined); onPreviewLanguageChange(Number(option.value)) }}
                options={[
                    { value: 0, label: 'Default' },
                    { value: ADD_TRANSLATION, label: 'Add new' },
                    { value: ALL_LANGUAGES, label: 'All available languages' },
                    ...translatedLanguages,
                ]}
                placeholder='Default'
            />
            {(previewLanguageId === ADD_TRANSLATION || isAllLanguages) && <>
                {!isAllLanguages && <SelectFormField
                    label='Add New Translation'
                    value={selectedLanguageId}
                    disabled={isRunning}
                    onChange={(option: SelectOptionModel) => setSelectedLanguageId(Number(option.value))}
                    options={availableLanguages}
                    placeholder='Select language to translate to'
                />}
                {isAllLanguages && <p>Translate to {availableLanguages.length} languages, one at a time. Existing translations will be refreshed.</p>}
                <button type='button' className='button has-glow' disabled={isRunning || (isAllLanguages ? availableLanguages.length === 0 : !selectedLanguageId)} onClick={addTranslation}>{isRunning ? 'Translating…' : 'Add'}</button>
                <button type='button' className='button has-glow' disabled={isRunning} style={{ background: 'white', color: 'black' }} onClick={() => { onPreviewLanguageChange(0); setSelectedLanguageId(undefined) }}>Cancel</button>
            </>}
        </>}
        {loadError && <p role='alert'>Could not refresh the translation list. <button type='button' disabled={isRunning} onClick={refreshTranslations}>Retry</button></p>}
    </div>
}

export default TourTranslations
